import React, { Fragment } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { connect } from "react-redux";
import { ReportBuilder } from "react-report-builder";
import "react-report-builder/lib/main.css";
import { match } from "react-router";
import { createMatchSelector } from "react-router-redux";
import "react-table/react-table.css";
import { AfterLoginTemplate } from "src/components/AfterLoginTemplate";
import { Spinner } from "src/components/Spinner";
import { routes } from "src/constants/routes";
import { IReport } from "src/models/report";
import { IAppState } from "src/models/state";
import { peekdataApi } from "src/services/api";
import { loadReport } from "src/state/report/actions";
import { getQueryVariable } from "src/utils/UrlUtils";
import { ReportSaveButton } from "./ReportSaveButton";

// #region -------------- Interfaces -------------------------------------------------------------------

interface IStateProps {
  reportID: string;
  defaultTab: number;
  reportError: string;
  reportIsLoading: boolean;
  reportTitle: string;
  report: IReport;
}

interface IDispatchProps {
  onLoadReport: (reportID: string) => void;
}

interface IOwnProps {}
interface IOwnState {
  paramsReportData: any;
  reportData: any;
}

interface IProps extends IStateProps, IDispatchProps, IOwnProps {}

// #endregion

// #region -------------- Component -------------------------------------------------------------------

const mergeReportRequestParams = (parsedParams) => {
  let data = {
    requestID: "nlp",
    consumerInfo: "Peekdata-Gateway-DataAPI-Demo",
    scopeName: parsedParams.scopeName,
    dataModelName: "Servicing-PostgreSQL",
    dimensions: parsedParams.dimensions,
    metrics: parsedParams.metrics,
    filters: parsedParams.filters,
    sortings: null,
    options: {
      rows: {
        limitRowsTo: "10",
      },
      arguments: {},
      connections: {},
      security: {},
    },
  };

  return data;
};
class ReportBuilderPage extends React.Component<IProps, IOwnState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      paramsReportData: {},
      reportData: {},
    };
  }

  public componentDidMount() {
    const { reportID, onLoadReport } = this.props;

    if (window.location.search.includes("reportData")) {
      let jsonString = new URLSearchParams(window.location.search).get(
        "reportData"
      );
      let paramsReportData = JSON.parse(jsonString);

      this.setState({
        paramsReportData,
        reportData: mergeReportRequestParams(paramsReportData),
      });
    }

    onLoadReport(reportID);
  }

  public componentDidUpdate(prevProps: IProps) {
    const { reportID, onLoadReport } = this.props;
    const prevReportId = prevProps && prevProps.reportID;

    if (prevReportId !== reportID) {
      onLoadReport(reportID);
    }
  }

  public render() {
    return (
      <AfterLoginTemplate>
        <div className="rb-report-builder-page">
          <div className="rb-content">
            <div className="container">
              {this.renderReportBuilder()}
              {this.renderSaveButton()}
            </div>
          </div>
        </div>
      </AfterLoginTemplate>
    );
  }

  // #region -------------- Report builder -------------------------------------------------------------------

  private renderReportBuilder = () => {
    const { defaultTab, report } = this.props;
    const reportRequest = report && report.dataRequest;

    if (this.showReportError()) {
      return this.renderReportError();
    }

    if (this.showReportLoader()) {
      return this.renderReportLoader();
    }

    return (
      <Fragment>
        {this.renderTitle()}
        <ReportBuilder
          peekdataApi={peekdataApi}
          defaultTab={defaultTab}
          reportRequest={
            Object.keys(this.state.reportData).length === 0
              ? reportRequest
              : this.state.reportData
          }
          baseURL="https://demo.peekdata.io:8443/datagateway/rest/v1"
        />
      </Fragment>
    );
  };

  // #endregion

  // #region -------------- Report error -------------------------------------------------------------------

  private renderReportError = () => {
    const { reportError } = this.props;

    if (!this.showReportError()) {
      return null;
    }

    return <div className="alert alert-danger">{reportError}</div>;
  };

  private showReportError = () => {
    const { reportError, reportIsLoading } = this.props;

    return reportError && !reportIsLoading;
  };

  // #endregion

  // #region -------------- Report loader -------------------------------------------------------------------

  private renderReportLoader = () => {
    if (!this.showReportLoader()) {
      return null;
    }

    return <Spinner />;
  };

  private showReportLoader = () => {
    const { reportIsLoading } = this.props;

    return reportIsLoading;
  };

  // #endregion

  // #region -------------- Title -------------------------------------------------------------------

  private renderTitle = () => {
    const { reportTitle } = this.props;
    const title = reportTitle ? reportTitle : "Report Builder Component";

    return <h1 className="rb-title-dark">{title}</h1>;
  };

  // #endregion

  // #region -------------- Operation buttons -------------------------------------------------------------------

  private renderSaveButton = () => {
    if (process.env.PEEKDATA_MANAGE_REPORTS === "false") {
      return null;
    }

    return <ReportSaveButton />;
  };

  // #endregion
}

// #endregion

// #region -------------- Connect -------------------------------------------------------------------

const connected = connect<IStateProps, IDispatchProps, IOwnProps, IAppState>(
  (state) => {
    const { router, report } = state;
    const pathMatch = matchSelector(state) as match<{ id: string }>;
    const params = pathMatch && pathMatch.params;
    const reportID = params && params.id;
    const searchParams = router && router.location && router.location.search;

    let defaultTab = 0;
    if (searchParams) {
      defaultTab = Number(
        getQueryVariable(searchParams.substring(1), "report-type")
      );
    }

    if (isNaN(defaultTab) || defaultTab > 1 || defaultTab < 0) {
      defaultTab = 0;
    }

    return {
      reportID,
      defaultTab,
      reportError: report && report.report && report.report.error,
      reportIsLoading: report && report.report && report.report.isFetching,
      reportTitle: report && report.reportTitle,
      report: report && report.report && report.report.data,
    };
  },
  (dispatch) => {
    return {
      onLoadReport: (reportID: string) => dispatch(loadReport(reportID)),
    };
  }
)(ReportBuilderPage);

export { connected as ReportBuilderPage };

// #endregion

// #region -------------- Selectors -------------------------------------------------------------------

const matchSelector = createMatchSelector(`${routes.report}/:id`);

// #endregion
